import React, { useEffect, useState, useContext } from 'react';
import { ExternalLink } from 'lucide-react';
import '../../ptSansFontFamily.css';
import '../../App.css';
import styles from './Sidebarlist.module.css';
import topAndBottomStyles from './SidebarTopAndBottom.module.css';
import { SidebarData } from './SidebarData';
import BlueLogo from '../../assets/BlueLogo.png';
import { Link, useLocation } from 'react-router-dom';
import { useMsal } from "@azure/msal-react";
import { loginRequest } from '../../authConfig';
import AdminContext from '../../Context/adminContext';
import GuestContext from '../../Context/guestContext';
import { useDispatch, useSelector } from '../../redux/store';
import { fetchGraphMePhotoDataUrl, fetchSidebarProfileTitle } from '../../redux/slices/sidebar';

import { SignOutButton } from "../Auth/SignOutButton";

/**
 * Renders the navbar component with a sign in or sign out button depending on whether or not a user is authenticated
 * @param props
 */

function StaffRole() {
  const dispatch = useDispatch();
  const { profileTitle } = useSelector((s) => s.sidebar);
  const { accounts } = useMsal();
  const account = accounts[0]; // If multiple accounts, select the one you want to use

  useEffect(() => {
    if (account) {
      const promise = dispatch(fetchSidebarProfileTitle({ email: account.username }));
      return () => promise?.abort?.();
    }
  }, [account, dispatch]);

  return (
    <div className={topAndBottomStyles.profileRole}>
      {profileTitle && <div>{profileTitle}</div>}
    </div>

  );
}

function SidebarItem({ val }) {
  const { pathname } = useLocation();
  const isActive = pathname === val.link || pathname.startsWith(val.link + '/');
  const isDisabled = val.disabled;

  const isExternalUrl = val.link.startsWith('http');
  // Use SPA navigation for internal routes; external links open in a new tab.
  const LinkComponent = isExternalUrl ? 'a' : Link;
  const linkProps = isExternalUrl
    ? { href: val.link, target: "_blank", rel: "noopener noreferrer" }
    : { to: val.link };

  return (
    <React.Fragment>
      <li>
        <LinkComponent
          {...linkProps}
          className={`${styles.row} ${isActive ? styles.ClickedOn : ''} ${isDisabled ? styles.disabled : styles.active}`}
          id={isActive ? styles.active : ''}
          onClick={(event) => {
            if (isDisabled) {
              event.preventDefault();
            }
          }}
        >
          <div
            id="icon"
            className={`${styles.iconSpace} ${isActive ? styles.iconClickedOn : ''} ${isDisabled ? styles.iconDisabled : ''}`}
          >
            {val.icon}
          </div>
          <div id="title" className={`${styles.listText} ${isActive ? styles.active : ''} ${isDisabled ? styles.disabled : ''}`}>
            {val.title}
            {isExternalUrl && (
              <ExternalLink
                className="ml-1 inline-block"
                size={12}
                aria-label="Opens in new tab"
                style={{ marginLeft: '6px' }}
              />
            )}
          </div>
        </LinkComponent>
      </li>
      {val.title === 'SharePoint' && <div className={styles.dottedLine}></div>}
    </React.Fragment>
  );
}

function Sidebar() {
  const { instance, accounts } = useMsal();
  const account = accounts[0]; // If multiple accounts, select the one you want to use
  const dispatch = useDispatch();
  const { photoDataUrl } = useSelector((s) => s.sidebar);
  // const [isAdmin, setIsAdmin] = useState(false);
  const isAdmin = useContext(AdminContext);
  const { isGuest, allowedPages } = useContext(GuestContext);
  const guestMode = !!isGuest;

  useEffect(() => {
    if (account) {
      let isActive = true;
      let photoPromise;

      instance
        .acquireTokenSilent({
          ...loginRequest,
          account: account,
        })
        .then((response) => {
          if (!isActive) return;
          photoPromise = dispatch(fetchGraphMePhotoDataUrl({ accessToken: response.accessToken }));
        })
        .catch((e) => {
          // Prevent unhandled promise rejections from MSAL failures (often non-Error objects)
          if (!isActive) return;
          // Ignore expected transient MSAL states
          if (e?.errorCode === 'interaction_in_progress') return;
          console.error('Sidebar acquireTokenSilent failed', e);
        });

      return () => {
        isActive = false;
        photoPromise?.abort?.();
      };
    }
  }, [account, instance, dispatch]);

  return (
    <div className={styles.Sidebar}>

      <div className={topAndBottomStyles.profileContainer}>
        {photoDataUrl ? <img src={photoDataUrl} alt="Profile" className={topAndBottomStyles.profileIcon} /> : null}
        <div className={topAndBottomStyles.profileInfo}>
          <div className={topAndBottomStyles.profilenName}>{account ? account.name : ''}</div>
          <StaffRole />
        </div>
      </div>

      <ul className={styles.SidebarList}>
        {(() => {
          const fallbackGuestTitles = new Set(['Profile']);
          const keyToTitle = {
            dashboard: 'Dashboard',
            profile: 'Profile',
            sharepoint: 'SharePoint',
            opportunities: 'Opportunities',
            projects: 'Projects',
            timesheets: 'Time Sheets',
            clients: 'Clients',
            invoicing: 'Invoicing',
            suppliers: 'Suppliers',
            admin: 'Admin',
            resourcing: 'Planner Board',
            planner: 'Planner Board',
          };

          const titlesFromApi = (Array.isArray(allowedPages))
            ? allowedPages.map(p => keyToTitle[(p || '').toString().trim().toLowerCase()]).filter(Boolean)
            : [];

          // Always allow Profile in UI
          titlesFromApi.push('Profile');

          const allowedTitleSet = new Set(titlesFromApi);
          const hasRestrictions = titlesFromApi.length > 0; // apply to all users if list provided

          const filtered = SidebarData.filter((val) => {
            if (val.title === 'Admin') {
              // Admin tab visible for admins by default, regardless of allowed list
              return isAdmin;
            }
            if (hasRestrictions) {
              return allowedTitleSet.has(val.title);
            }
            return true;
          });
          return filtered.map((val, key) => (
            <SidebarItem key={key} val={val} />
          ));
        })()}
      </ul>

      <div className={topAndBottomStyles.SidebarBottom}>
        <SignOutButton />
        <div className={topAndBottomStyles.BlueLogoContainer}><img src={BlueLogo} className={topAndBottomStyles.blueLogo} /></div>
      </div>

    </div>
  );
}

export default Sidebar;
